import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const assetUrl = (url) => url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null;
const TF_LOGO = "https://www.targetfunder.com/tf_logo.png";

export default function PrivacyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);

  useEffect(() => {
    axios.get(`${API}/clubs/${slug}`).then(r => setClub(r.data)).catch(() => {});
  }, [slug]);

  if (!club) return <div className="min-h-screen flex items-center justify-center text-[#1D3557]/40">Loading...</div>;

  // Privacy contact intentionally routes via TF support (not the club admin's
  // personal email) so the club admin's email isn't published on a crawler-
  // accessible page. TF then forwards any GDPR enquiries to the right club.
  const contactEmail = 'support@targetfunder.com';

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#1D3557]/5 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {club.logo_url && (
              <img src={assetUrl(club.logo_url)} alt={club.name} className="w-8 h-8 object-contain" />
            )}
            <h1 className="text-lg font-bold text-[#1D3557]">{club.name}</h1>
          </div>
          <button
            onClick={() => navigate(`/worldcup2026/${slug}`)}
            className="text-xs font-bold uppercase tracking-widest text-[#1D3557]/40 hover:text-[#1D3557] transition-colors"
            data-testid="back-to-club-btn"
          >
            Back to Predictor
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="privacy-content">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E63946] mb-3">Legal</p>
        <h2 className="font-black text-3xl sm:text-4xl uppercase tracking-tight leading-none text-[#1D3557] mb-8">
          Privacy & Data Protection
        </h2>
        
        <div className="text-[#1D3557]/55 space-y-6 text-sm leading-relaxed">
          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Data Controller</h3>
            <p>{club.name} is the Data Controller responsible for your personal data. TargetFunder acts as the Data Processor, providing the infrastructure to manage your entry.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Platform Operator</h3>
            <p>TargetFunder (targetfunder.com) is owned and operated by Castleview Resources Ltd, Unit 3 Grange Road Retail Park, Dublin 16, D16 XD82, Ireland. VAT ID: IE4271347SH.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Data Collected</h3>
            <p>We collect your Name and Email Address. This is the minimum data required to manage your entry and display your position on the live leaderboard.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Lawful Basis for Processing</h3>
            <p>We process your data under Performance of a Contract (to facilitate your entry into the competition and send you an entry confirmation email via Resend).</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Third-Party Processors</h3>
            <p>Your payment data is handled exclusively by Stripe; the Club and TargetFunder do not store your credit card details. Your name and email are processed by TargetFunder and sent via Resend for your entry confirmation email only.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Data Retention</h3>
            <p>Your personal data is retained for the duration of the competition and for 30 days after the World Cup 2026 Final to allow for prize distribution and any tournament-related correspondence. You may request earlier deletion at any time by contacting the Club.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Your Rights</h3>
            <p>You have the right to access, correct, or request the deletion of your data at any time. To exercise these rights, please contact the Club at <a href={`mailto:${contactEmail}`} className="underline text-[#1D3557] hover:text-[#E63946] transition-colors">{contactEmail}</a>.</p>
          </div>
        </div>
      </main>

      <footer className="py-6 px-6 border-t border-[#1D3557]/5">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-3">
          <p className="text-[#1D3557]/30 text-sm">Supporting {club.name} through World Cup 2026</p>
          <a
            href="https://www.targetfunder.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#F8FAFA] border border-[#1D3557]/5 hover:border-[#1D3557]/15 transition-colors"
            data-testid="targetfunder-badge"
          >
            <img src={TF_LOGO} alt="TargetFunder" className="h-5 w-5 object-contain" />
            <span className="font-heading font-black text-sm uppercase tracking-tighter leading-none text-[#1D3557]/40">
              Made by <span className="text-[#1D3557]">target</span><span className="text-[#E63946]">funder</span><span className="text-[#1D3557]/30">.com</span>
            </span>
          </a>
        </div>
      </footer>
    </div>
  );
}
